"use client";

import React, { useState } from "react";
import Link from "next/link";
import { GiCheckMark } from "react-icons/gi";
import { FiRefreshCw } from "react-icons/fi";
import { ChevronDown, ChevronRight } from "lucide-react";
import SnappedWidth from "~/components/ui/snapped-width";
import RowPoints from "~/components/detailed-stats/row-points";

import type { DetailedStatisticsCategory } from "~/lib/detailedStats";
import { StatsCategoryType, formatPercentage } from "~/lib/detailedStats";

type Task = {
  problemId: number;
  name: string;
};

type Props = {
  category: DetailedStatisticsCategory;
  denominator: number;
  isFullMode: boolean;
  problemSetId: number | string;
  onClose: () => void;
  idx: number;
};

export default function RetryGroupItem({
  category: cat,
  denominator,
  isFullMode,
  problemSetId,
  onClose,
  idx,
}: Props) {
  const [isExpanded, setIsExpanded] = useState(false);
  const earnedPoints = cat.earnedPoints ?? 0;
  const maxPoints = cat.maxPoints ?? 0;

  return (
    <div key={`cat-${idx}`} className="mb-3">
      <div
        className="grid cursor-pointer grid-cols-[auto_1fr] items-start gap-4"
        onClick={() => setIsExpanded((v) => !v)}
        role="button"
        data-testid={`category-row-${idx}`}
        aria-expanded={isExpanded}
      >
        <SnappedWidth className="flex items-center gap-1 whitespace-nowrap">
          <div className="flex items-center text-pink-400">
            {isExpanded ? (
              <ChevronDown data-testid={`chevron-${idx}`} className="h-4 w-4" />
            ) : (
              <ChevronRight
                data-testid={`chevron-${idx}`}
                className="h-4 w-4"
              />
            )}
            <span className="ml-1">
              <FiRefreshCw key={`refresh-${idx}`} />
            </span>
          </div>
          <span key={`colon-${idx}`}>:</span>
        </SnappedWidth>

        <div className="flex items-start gap-2">
          <span>{cat.count}</span>
          <span>({formatPercentage(cat.count, denominator)}%)</span>
          <RowPoints earnedPoints={earnedPoints} maxPoints={maxPoints} />

          {isFullMode && (cat.tasks ?? []).length > 0 && (
            <span className="ml-2 inline-block text-sm text-muted-foreground">
              (
              {cat.tasks.map((t: Task, i: number) => (
                <React.Fragment key={`task-${t.problemId}`}>
                  <Link
                    href={`/problem-sets/${problemSetId}/problems/${t.problemId}`}
                    onClick={() => onClose()}
                    className="text-xs text-white underline"
                  >
                    {t.name}
                  </Link>
                  {i < (cat.tasks ?? []).length - 1 ? ", " : null}
                </React.Fragment>
              ))}
              )
            </span>
          )}
        </div>
      </div>

      {isExpanded && (cat.subCategories ?? []).length > 0 && (
        <div className="mt-2 pl-6">
          {(cat.subCategories ?? []).map((sub, sidx) => (
            <div
              key={`sub-${idx}-${sidx}`}
              className="mb-2 grid grid-cols-[auto_1fr] items-start gap-4"
            >
              <SnappedWidth className="flex items-center gap-1 whitespace-nowrap">
                <SnappedWidth className="flex items-center gap-1 whitespace-nowrap">
                  {sub.type === StatsCategoryType.WIN && (
                    <GiCheckMark className="text-green-500" />
                  )}
                  {sub.type === StatsCategoryType.DRAW && (
                    <span className="text-yellow-300">½</span>
                  )}
                  {sub.type === StatsCategoryType.EXTERNAL_HINT && (
                    <span className="text-blue-500">½</span>
                  )}

                  {(sub.retryCount ?? 0) > 0 && (
                    <div className="flex items-center text-pink-400">
                      <FiRefreshCw />
                      <span className="ml-1">{String(sub.retryCount)}</span>
                    </div>
                  )}

                  <span key={`colon-sub-${idx}-${sidx}`}>:</span>
                </SnappedWidth>
              </SnappedWidth>
              <div className="flex items-start gap-2">
                <span>{sub.count}</span>
                <span>({formatPercentage(sub.count, denominator)}%)</span>
                <RowPoints
                  earnedPoints={sub.earnedPoints ?? 0}
                  maxPoints={sub.maxPoints ?? 0}
                />

                {isFullMode && (sub.tasks ?? []).length > 0 && (
                  <span className="ml-2 inline-block text-sm text-muted-foreground">
                    (
                    {sub.tasks.map((t: Task, ti: number) => (
                      <React.Fragment key={`subtask-${t.problemId}`}>
                        <Link
                          href={`/problem-sets/${problemSetId}/problems/${t.problemId}`}
                          onClick={() => onClose()}
                          className="text-xs text-white underline"
                        >
                          {t.name}
                        </Link>
                        {ti < (sub.tasks ?? []).length - 1 ? ", " : null}
                      </React.Fragment>
                    ))}
                    )
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
